import { Group } from '@/components/_common';
import ItemsStockModal from '@/components/_common/ItemsStockModal';
import {
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useDisclosure } from '@/hooks';
import {
  Button,
  Empty,
  Form,
  FormListOperation,
  Modal,
  ModalProps,
  Tooltip,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useInvoiceCFEFormContext } from '../../InvoiceCFEFormProvider';
import {
  TaxCouponItem,
  TaxCouponItemType,
} from '@/core/domain/coupon/coupons/coupons.types';
import {
  useTaxCouponItemCreate,
  useTaxCouponItemEdit,
} from '@/core/domain/coupon/coupons/coupons.hooks';
import InvoiceCFEItemCard from './ItemCard';
import { useNcmMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { usePartMutation } from '@/core/domain/operational/Parts/Parts.hooks';
import { NamePath } from 'antd/lib/form/interface';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { taxesIcmsCsosnOptions } from '@/components/_common/TaxesSelect/TaxeIcmsCsosnSelect';
import { useCouponCompanyConfigDetail } from '@/core/domain/coupon/company/company.hooks';
import { Ncm } from '@/core/domain/operational/Ncm/Ncm.types';
import SelectTasksListModal from '@/components/_common/SelectTasksListModal';
import { Task } from '@/core/domain/operational/Tasks/Tasks.types';
import { useTaskMutation } from '@/core/domain/operational/Tasks/Tasks.hooks';

interface Props extends ModalProps {
  onCancel: () => void;
  item?: TaxCouponItem;
}

export interface CouponItemFormValues {
  items?: TaxCouponItem[];
}

interface TabError {
  general: boolean;
  taxes: boolean;
  index: number;
}

const InvoiceCFEItemModal: React.FC<Props> = ({ item, ...props }) => {
  const { taxCoupon } = useInvoiceCFEFormContext();
  const [form] = Form.useForm<CouponItemFormValues>();
  const formValues = Form.useWatch([], form);

  const isEdit = !!item;

  const { data: company } = useCompany(taxCoupon?.companyId);

  const [tabErrors, setTabErrors] = useState<TabError[]>([]);

  const [stockIdsSelected, setStockIdsSelected] = useState<number[]>([]);
  const [taskIdsSelected, setTaskIdsSelected] = useState<number[]>([]);

  const [partsModalOpen, partsModalAction] = useDisclosure(false, {
    onClose: () => setStockIdsSelected([]),
  });

  const [tasksModalOpen, tasksModalAction] = useDisclosure(false, {
    onClose: () => setTaskIdsSelected([]),
  });

  const taxCouponItemCreate = useTaxCouponItemCreate();
  const taxCouponItemEdit = useTaxCouponItemEdit();
  const partDetail = usePartMutation();
  const ncmDetail = useNcmMutation();
  const taskDetail = useTaskMutation();

  const { data: couponCompanyConfig } = useCouponCompanyConfigDetail(
    taxCoupon?.companyId,
  );

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
    }
  }, [formValues, props.open]);

  useEffect(() => {
    if (item && props.open) {
      form.setFieldsValue({ items: [item] });
    }
  }, [item, props.open]);

  function handleClose() {
    props.onCancel();
    setTabErrors([]);
  }

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (!values?.items?.length) {
        return message.warning('Informe um ou mais itens');
      }

      if (taxCoupon) {
        if (!isEdit) {
          await taxCouponItemCreate.mutateAsync({
            taxCouponId: taxCoupon.id,
            couponItems: values?.items?.map((item) => ({
              ...item,
              fuel: Object.values(item?.fuel || {}).every((value) => !value)
                ? undefined
                : item.fuel,
              taxes: {
                ...item.taxes,
                issqn: Object.values(item?.taxes?.issqn || {}).every(
                  (value) => !value,
                )
                  ? undefined
                  : item.taxes?.issqn,
              },
            })),
          });
        } else {
          const item = values?.items[0];

          if (item) {
            await taxCouponItemEdit.mutateAsync({
              taxCouponId: taxCoupon.id,
              ...item,
              fuel: Object.values(item?.fuel || {}).every((value) => !value)
                ? undefined
                : item.fuel,
              taxes: {
                ...item.taxes,
                issqn: Object.values(item?.taxes?.issqn || {}).every(
                  (value) => !value,
                )
                  ? undefined
                  : item.taxes?.issqn,
              },
            });
          }
        }

        handleClose();
      }
    } catch (error: unknown) {
      const formData = error as { errorFields?: { name: NamePath[] }[] };

      if (formData?.errorFields && formData.errorFields.length) {
        const errors = formData.errorFields.reduce<TabError[]>((acc, curr) => {
          const hasError = acc.find((err) => err.index === curr.name[1]);
          const index = curr.name[1] as number;
          const hasTaxes = curr.name[2] === 'taxes';

          if (!hasError) {
            return [
              ...acc,
              {
                index,
                general: !hasTaxes,
                taxes: hasTaxes,
              },
            ];
          }

          return acc.map((err) => ({
            ...err,
            general: err.index === index && !hasTaxes ? true : err.general,
            taxes: err.index === index && hasTaxes ? true : err.taxes,
          }));
        }, []);

        setTabErrors(errors);

        message.warning('Preencha todos os campos obrigatórios');
      }
    }
  }

  const handlePartAdd = useCallback(
    async (item: StockItem, actionAdd: FormListOperation['add']) => {
      setStockIdsSelected((prev) => [...prev, item.partId]);

      const unitValue = item.prices.find(
        (price) => price.company.id === taxCoupon?.companyId,
      )?.price;

      const itemFinded = formValues?.items?.find(
        (formItem) => formItem.itemId === item.partId,
      );

      if (
        itemFinded ||
        (partDetail.isLoading && partDetail.variables === item.partId)
      )
        return;

      const part = await partDetail.mutateAsync(item.partId);

      let ncm = {} as Ncm | undefined;

      if (part.ncmId) {
        ncm = await ncmDetail.mutateAsync(part.ncmId);
      }

      const hasSimpleNational = company?.taxRegimeId === 2;

      let csosnIcms: undefined | string = ncm?.csosnIcms;
      let cstIcms: undefined | string = ncm?.cstIcms;

      const partConfig = couponCompanyConfig?.defaultTaxConfig.partConfig;

      if (ncm?.csosnIcms) {
        csosnIcms = taxesIcmsCsosnOptions.find(
          (option) => option.label === ncm?.csosnIcms,
        )?.value;
      } else {
        csosnIcms = partConfig?.icms?.csosn;
      }

      if (ncm?.cstIcms) {
        cstIcms = taxesIcmsCsosnOptions.find(
          (option) => option.label === ncm?.cstIcms,
        )?.value;
      } else {
        cstIcms = partConfig?.icms?.cst;
      }

      const data = {
        taxes: {
          cofins: {
            cst: ncm?.cstCofins || partConfig?.cofins.cst,
            rate: ncm?.aliquotCofins || partConfig?.cofins.rate,
            baseCalculation: unitValue,
          },
          pis: {
            cst: ncm?.cstPis || partConfig?.pis.cst,
            rate: ncm?.aliquotPis || partConfig?.pis.rate,
            baseCalculation: unitValue,
          },
          icms: {
            csosn: hasSimpleNational ? csosnIcms : undefined,
            cst: !hasSimpleNational ? cstIcms : undefined,
            rate: ncm?.aliquotIcms || partConfig?.icms.rate,
            origin: partConfig?.icms.origin,
          },
        },
      } as TaxCouponItem;

      actionAdd({
        ...data,
        taxes: {
          ...data.taxes,
          ncmId: part.ncmId,
          cfopId: part.cfopId || partConfig?.cfopId,
        },
        unitMeasure: part.unit || partConfig?.unitMeasure,
        itemId: item.partId,
        description: item.description,
        type: TaxCouponItemType.PART,
        quantity: 1,
        unitValue,
      });

      partsModalAction.close();
    },
    [formValues, partDetail],
  );

  const handleTaskAdd = useCallback(
    async (item: Task, actionAdd: FormListOperation['add']) => {
      setTaskIdsSelected((prev) => [...prev, item.id]);

      const unitValue = item.taskPriceSet.find(
        (price) => price.companyId === taxCoupon?.companyId,
      )?.price;

      const itemFinded = formValues?.items?.find(
        (formItem) => formItem.itemId === item.id,
      );

      if (
        itemFinded ||
        (taskDetail.isLoading && taskDetail.variables === item.id)
      )
        return;

      const task = await taskDetail.mutateAsync(item.id);

      const hasSimpleNational = company?.taxRegimeId === 2;

      const taskConfig = couponCompanyConfig?.defaultTaxConfig.taskConfig;

      const data = {
        taxes: {
          cofins: {
            cst: taskConfig?.cofins.cst,
            rate: taskConfig?.cofins.rate,
            baseCalculation: unitValue,
          },
          pis: {
            cst: taskConfig?.pis.cst,
            rate: taskConfig?.pis.rate,
            baseCalculation: unitValue,
          },
          icms: {
            csosn: hasSimpleNational ? taskConfig?.icms?.csosn : undefined,
            cst: !hasSimpleNational ? taskConfig?.icms?.cst : undefined,
            rate: taskConfig?.icms.rate,
            origin: taskConfig?.icms.origin,
          },
          issqn: { ...taskConfig?.issqn },
        },
      } as TaxCouponItem;

      actionAdd({
        ...data,
        taxes: {
          ...data.taxes,
          cfopId: taskConfig?.cfopId,
        },
        unitMeasure: task.unit || taskConfig?.unitMeasure,
        itemId: item.id,
        description: item.description,
        type: TaxCouponItemType.TASK,
        quantity: 1,
        unitValue,
      });

      partsModalAction.close();
    },
    [formValues, taskDetail],
  );

  const getItemsIds = useCallback(
    (type: TaxCouponItemType) =>
      [
        ...(type === TaxCouponItemType.PART
          ? stockIdsSelected
          : taskIdsSelected),

        ...(formValues?.items
          ?.filter((item) => item.type === type)
          ?.map((item) => item.itemId) || []),

        ...(taxCoupon?.couponItems
          ?.filter((item) => item.type === type)
          ?.map((item) => item.itemId) || []),
      ]?.reduce<number[]>((acc, currId) => {
        const hasItem = acc.some((itemId) => itemId === currId);

        if (!hasItem) {
          return [...acc, currId];
        }

        return acc;
      }, []),
    [
      stockIdsSelected,
      taskIdsSelected,
      formValues?.items,
      taxCoupon?.couponItems,
    ],
  );

  return (
    <>
      <Modal
        {...props}
        onCancel={handleClose}
        onOk={handleSubmit}
        confirmLoading={
          taxCouponItemCreate.isLoading || taxCouponItemEdit.isLoading
        }
        title={isEdit ? 'Editar item' : 'Adicionar itens do cupom fiscal'}
        width={900}
        okText="Salvar"
      >
        <Form form={form}>
          <Form.List name="items" initialValue={[]}>
            {(fieldList, action) => (
              <>
                {!fieldList.length && !isEdit && (
                  <Empty
                    description={
                      <>
                        <p>Não há itens. Selecione uma peça ou serviço!</p>
                      </>
                    }
                  />
                )}

                {fieldList.map((_, index) => {
                  return (
                    <InvoiceCFEItemCard
                      isEdit={isEdit}
                      key={index}
                      action={action}
                      index={index}
                      tabError={tabErrors[index]}
                    />
                  );
                })}

                {!isEdit && (
                  <Group position="center" gap={16}>
                    <Button
                      type="primary"
                      ghost
                      onClick={partsModalAction.open}
                      icon={<FaPlus size={10} />}
                    >
                      Adicionar peça
                    </Button>

                    <Tooltip title="Cupom de serviço está em processo de análise interna.">
                      <Button
                        type="primary"
                        ghost
                        onClick={tasksModalAction.open}
                        icon={<FaPlus size={10} />}
                        disabled
                      >
                        Adicionar serviço
                      </Button>
                    </Tooltip>
                  </Group>
                )}

                <ItemsStockModal
                  itemType={StockItemType.Part}
                  handleVisible={partsModalAction.close}
                  onSelectItem={(item) => handlePartAdd(item, action.add)}
                  visible={partsModalOpen && !!props.open}
                  itemsSelected={getItemsIds(TaxCouponItemType.PART)}
                />

                <SelectTasksListModal
                  itemSelectedIds={getItemsIds(TaxCouponItemType.TASK)}
                  onCallback={(tasks) => {
                    const task = tasks.at(-1);
                    if (task) {
                      handleTaskAdd(task, action.add);
                    }
                  }}
                  onCancel={tasksModalAction.close}
                  open={tasksModalOpen}
                  multiple={false}
                />
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceCFEItemModal;
